import {Injectable} from '@angular/core';
import { Culture } from '../models/entities/culture';


@Injectable({providedIn: 'root'})
export class CultureService {
  constructor() {
  }
  
  //the culture is set in the main.ts file of the application, so it is available before the app is initialized. 
  public getCulture(): Culture {
    var json = sessionStorage.getItem('culture');

    if (json != null)
    {
      return JSON.parse(json);
    }

    throw new Error('No culture has been set.');
  }

  public formatCurrency(value: number | string | null | undefined, decimals?: number): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const culture = this.getCulture();
    
    const numericValue = this.toNumber(value);

    if (isNaN(numericValue)) {
      throw new Error('Invalid number provided for currency formatting');
    }

    decimals ??= culture.CurrencyDecimalDigits;

    const formatter = new Intl.NumberFormat(culture.Locale, {
      style: 'currency',
      currency: culture.CurrencyCode,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });

    return formatter.format(numericValue);
  }

  public parseCurrency(value: string | number, decimals?: number): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    
    const culture = this.getCulture();
    const decimalSeparator = this.getDecimalSeparator(culture.Locale);
    decimals ??= culture.CurrencyDecimalDigits;
    
    // If already a number, just format it with locale decimal separator
    if (typeof value === 'number') {
      return value.toFixed(decimals).replace('.', decimalSeparator);
    }
    
    // Remove currency symbols, whitespace, and other non-numeric characters
    const cleaned = value.replace(/[^\d.,-]/g, '');
    
    // Find the last separator (. or ,) - this is the decimal separator
    const lastSepIndex = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));
    
    if (lastSepIndex === -1) {
      // No decimal separator found
      const num = parseInt(cleaned, 10);
      return isNaN(num) ? '' : num.toFixed(decimals).replace('.', decimalSeparator);
    }
    
    // Everything before last separator is integer part (remove all separators)
    // Everything after is decimal part
    const integerPart = cleaned.substring(0, lastSepIndex).replace(/[.,]/g, '');
    const decimalPart = cleaned.substring(lastSepIndex + 1);
    
    const normalized = `${integerPart}.${decimalPart}`;
    const numericValue = parseFloat(normalized);
    
    return isNaN(numericValue) ? '' : numericValue.toFixed(decimals).replace('.', decimalSeparator);
  }

  public parseNumber(value: string | number, decimals: number): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    
    // If already a number, just format it
    if (typeof value === 'number') {
      return value.toFixed(decimals);
    }
    
    // Remove non-numeric characters except digits, dots, commas, and minus
    const cleaned = value.replace(/[^\d.,-]/g, '');
    
    // Find the last separator (. or ,) - this is the decimal separator
    const lastSepIndex = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));
    
    if (lastSepIndex === -1) {
      // No decimal separator found
      const num = parseInt(cleaned, 10);
      return isNaN(num) ? '' : num.toFixed(decimals);
    }
    
    // Everything before last separator is integer part (remove all separators)
    // Everything after is decimal part
    const integerPart = cleaned.substring(0, lastSepIndex).replace(/[.,]/g, '');
    const decimalPart = cleaned.substring(lastSepIndex + 1);
    
    const normalized = `${integerPart}.${decimalPart}`;
    const numericValue = parseFloat(normalized);
    
    return isNaN(numericValue) ? '' : numericValue.toFixed(decimals);
  }

  public formatNumber(value: number | string, decimals: number): string {
    const culture = this.getCulture();
    const numericValue = this.toNumber(value);

    if (isNaN(numericValue)) {
      return '';
    }

    const formatter = new Intl.NumberFormat(culture.Locale, {
      style: 'decimal',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });

    return formatter.format(numericValue);
  }

  public toNumber(value: number | string): number {
    if (typeof value === 'number') {
      return value;
    }

    const decimalSeparator = this.getDecimalSeparator(this.getCulture().Locale);
    const cleaned = value.replace(/[^\d.,-]/g, '');

    // Without the culture's decimal separator the value is treated as invariant (e.g. "12.5" written by a number input)
    if (!cleaned.includes(decimalSeparator)) {
      return parseFloat(cleaned.replace(/,/g, ''));
    }

    const groupSeparator = decimalSeparator === ',' ? '.' : ',';
    return parseFloat(cleaned.split(groupSeparator).join('').replace(decimalSeparator, '.'));
  }

  private getDecimalSeparator(locale: string): string {
    const numberWithDecimal = 1.1;
    const formatted = Intl.NumberFormat(locale).format(numberWithDecimal);
    return formatted.charAt(1); // Get the character between the two 1s
  }
}